import React, { useState, useEffect } from 'react';
import { forEach } from 'lodash';
import styled from 'styled-components';
import { FormControlLabel, InputLabel, Tooltip } from '@mui/material';

import Checkbox from '../Checkbox/Checkbox';
import ChargersTask from '../../resources/ChargersTask';
import { makeMenuItems } from '../Helper/helper';
import Select from '../Select/Select';
import { DIAGNOSTICS_CHARGER_TASK_TYPE } from '../../constants/chargerTask';
import { Error } from './style';
import { makeErrorHandler } from '../../resources/helper';

const MAX_CATEGORY_NAME_LENGTH = 16;

const synthecticCategory = {
  category: 'ALL',
  parameters: ['TRACE'],
};

export const substr = (str, maxLength = MAX_CATEGORY_NAME_LENGTH) => (str.length > maxLength ? `${str.substr(0, maxLength)}...` : str);

const makeValue = (items) =>
  Object.keys(items)
    .filter((category) => items[category].selected)
    .map((category) => ({ category, parameter: items[category].parameter }));

const makeActiveItems = (categories) => {
  const activeItems = {};

  forEach(categories, (item) => (activeItems[item.category] = { selected: true, parameter: item.parameter }));

  return activeItems;
};

function DiagnosticCategoriesControl(props) {
  const { name, label, chargerId, onChange, defaultValue = [] } = props;
  const [state, setState] = useState({
    diagnosticCategories: [],
    isAllSelected: false,
    activeItems: makeActiveItems(defaultValue),
    error: '',
  });

  const { isAllSelected, diagnosticCategories, activeItems, error } = state;

  const setError = (error) => setState({ ...state, error });
  const onChangeTrigger = (category, isAll) => (event, IsCategoryParamSelected) => {
    const updatedActiveItems = { ...activeItems };

    if (isAll) {
      if (IsCategoryParamSelected) {
        forEach(Object.keys(updatedActiveItems), (item) => {
          updatedActiveItems[item].selected = false;
        });
        updatedActiveItems[synthecticCategory.category] = { selected: true, parameter: synthecticCategory.parameters[0] };
      } else {
        updatedActiveItems[synthecticCategory.category] = { selected: false, parameter: synthecticCategory.parameters[0] };
      }
    } else if (IsCategoryParamSelected) {
      const categoryItem = updatedActiveItems[category.category];

      if (!categoryItem) {
        updatedActiveItems[category.category] = { selected: true, parameter: category.parameters[0] };
      } else {
        updatedActiveItems[category.category].selected = true;
      }
    } else {
      updatedActiveItems[category.category].selected = false;
    }

    setState((state) => ({ ...state, activeItems: updatedActiveItems, isAllSelected: isAll && IsCategoryParamSelected }));

    onChange({ target: { name, type: 'diagnostics', value: makeValue(updatedActiveItems) } });
  };
  const onChangeSelect = (category) => (event) => {
    const { value } = event.target;
    const updatedActiveItems = { ...activeItems };

    updatedActiveItems[category.category].parameter = value;

    setState((state) => ({ ...state, activeItems: updatedActiveItems }));

    onChange({ target: { name, type: 'diagnostics', value: makeValue(updatedActiveItems) } });
  };

  useEffect(() => {
    ChargersTask.interactions({ chargerId })
      .then(({ data }) => {
        const dataCategory = data.find((item) => item.type === DIAGNOSTICS_CHARGER_TASK_TYPE);

        if (dataCategory) {
          const diagnosticCategories = dataCategory.diagnostics;

          setState((state) => ({
            ...state,
            diagnosticCategories,
            isAllSelected: false,
          }));
        }

        if (
          defaultValue &&
          defaultValue.length === 1 &&
          defaultValue[0].category === synthecticCategory.category &&
          defaultValue[0].parameter === synthecticCategory.parameters[0]
        ) {
          onChangeTrigger(synthecticCategory, true)(null, true);
        }

        if (!defaultValue || defaultValue.length === 0) {
          onChangeTrigger(synthecticCategory, true)(null, true);
        }
      })
      .catch((err) => {
        onChangeTrigger(synthecticCategory, true)(null, true);
        makeErrorHandler(setError, { 404: 'Interactions for charger not found' })(err);
      });
  }, []);

  const renderCategory = (category, isAll = false) => {
    const isCheckboxChecked =
      (isAllSelected && isAll) || (activeItems[category.category] ? activeItems[category.category].selected : false);

    return (
      <Category key={category.category}>
        {category.category.length > MAX_CATEGORY_NAME_LENGTH ? (
          <Tooltip title={category.category} placement="top">
            <FormControlLabel
              onChange={onChangeTrigger(category, isAll)}
              control={<Checkbox color="primary" checked={isCheckboxChecked} disabled={!isAll && isAllSelected} />}
              label={substr(category.category)}
            />
          </Tooltip>
        ) : (
          <FormControlLabel
            onChange={onChangeTrigger(category, isAll)}
            control={<Checkbox checked={isCheckboxChecked} color="primary" disabled={!isAll && isAllSelected} />}
            label={category.category}
          />
        )}

        <Select
          value={activeItems[category.category] ? activeItems[category.category].parameter : category.parameters[0]}
          disabled={!activeItems[category.category] || !activeItems[category.category].selected}
          onChange={onChangeSelect(category)}
        >
          {makeMenuItems(category.parameters.map((parameter) => ({ value: parameter, label: parameter })))}
        </Select>
      </Category>
    );
  };

  return (
    <Wrap>
      <InputLabel shrink>{label}</InputLabel>
      {renderCategory(synthecticCategory, true)}
      <br />
      {error ? (
        <Error>{error}</Error>
      ) : (
        <List>
          {diagnosticCategories.sort((prev, next) => (prev.category > next.category ? 1 : -1)).map((item) => renderCategory(item))}
        </List>
      )}
    </Wrap>
  );
}

export const Wrap = styled.div`
  display: flex;
  flex-direction: column;

  .MuiFormControlLabel-root {
    margin-left: 0px;
  }
`;

export const List = styled.div`
  display: flex;
  flex-direction: column;
  margin: 5px 0 0 0;
  max-height: 168px;
  overflow-y: auto;
  overflow-x: visible;
`;

export const Category = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: space-between;

  .MuiCheckbox-root {
    padding: 0;
  }

  .MuiInputBase-root {
    width: 75px;
    margin-top: 0;
  }
`;

export default DiagnosticCategoriesControl;
